import { z } from 'zod/v4';
import {
  DOC_TYPE_CODES, OCCUPANCY_CODES, LOAN_PURPOSE_CODES, CITIZENSHIP_CODES,
  PROPERTY_TYPE_CODES, CREDIT_EVENT_CODES, US_STATES,
} from './vocabulary.ts';
import type { Scenario } from './intake.ts';

/**
 * The rule trigger language.
 *
 * Closed and non-Turing-complete on purpose. A rule decides whether a broker is told
 * their file can be done, it is authored through admin dropdowns by someone who is not
 * an engineer, and it is stored as data - so the language has to be one where every
 * expressible program terminates, touches nothing outside the intake, and can be read
 * back into the same dropdowns it was built from. An expression language would be more
 * flexible and would make all three of those false.
 *
 * It compiles to TypeScript (evaluated per request) and to SQL (used to prefilter
 * candidate rules before any model call). The two are tested to agree on every field
 * and operator, because a rule that prefilters in but evaluates out is invisible - it
 * simply never fires, and nobody finds out until a broker gets the wrong answer.
 */

/** Fields a rule may test. Deliberately the intake, and nothing derived downstream. */
export const PREDICATE_FIELDS = {
  loanPurpose:   { type: 'enum',    values: LOAN_PURPOSE_CODES },
  occupancy:     { type: 'enum',    values: OCCUPANCY_CODES },
  propertyType:  { type: 'enum',    values: PROPERTY_TYPE_CODES },
  docType:       { type: 'enum',    values: DOC_TYPE_CODES },
  citizenship:   { type: 'enum',    values: CITIZENSHIP_CODES },
  creditEvent:   { type: 'enum',    values: CREDIT_EVENT_CODES },
  state:         { type: 'enum',    values: US_STATES },
  ficoScore:     { type: 'number' },
  loanAmount:    { type: 'number' },
  propertyValue: { type: 'number' },
  ltv:           { type: 'number' },
  dti:           { type: 'number' },
  dscr:          { type: 'number' },
  reservesMonths:{ type: 'number' },
  creditEventSeasoningMonths: { type: 'number' },
  // Null on a purchase, and null on a cash-out where the broker left it blank - both of
  // which `matches` already treats as "unknown, do not fire", so a cap rule cannot
  // invent a figure for a file that never stated one.
  cashInHand:    { type: 'number' },
} as const;

export type PredicateField = keyof typeof PREDICATE_FIELDS;
export const PREDICATE_FIELD_NAMES = Object.keys(PREDICATE_FIELDS) as PredicateField[];

const NUMBER_OPS = ['eq', 'ne', 'lt', 'lte', 'gt', 'gte'] as const;
const ENUM_OPS = ['eq', 'ne', 'in', 'not_in'] as const;

const fieldEnum = z.enum(PREDICATE_FIELD_NAMES as [PredicateField, ...PredicateField[]]);

const comparison = z.object({
  field: fieldEnum,
  op: z.enum([...NUMBER_OPS, ...ENUM_OPS] as [string, ...string[]]),
  value: z.union([z.number(), z.string(), z.array(z.string()), z.array(z.number())]),
});

export type Comparison = z.infer<typeof comparison>;

/**
 * One level of nesting, not arbitrary recursion.
 *
 * Every real trigger in Henry's fifteen is a conjunction of comparisons, sometimes with
 * one disjunctive group inside it ("DSCR or foreign national, AND fico below 660").
 * Allowing arbitrary depth would buy expressiveness nobody has asked for and cost a
 * dropdown UI that can render what it stores.
 */
export const predicateSchema = z.object({
  all: z.array(z.union([
    comparison,
    z.object({ any: z.array(comparison).min(1) }),
  ])).default([]),
});

export type Predicate = z.infer<typeof predicateSchema>;

/** A predicate with no conditions matches everything; a rule like that is a bug. */
export const isEmptyPredicate = (p: Predicate): boolean => p.all.length === 0;

/* ------------------------------------------------------------------ validation */

export interface PredicateError { path: string; message: string }

/**
 * Check a predicate against the field registry.
 *
 * Zod validates the shape; this validates the meaning - that `ficoScore` is compared to
 * a number, that `citizenship in [...]` names real citizenships. An admin who typos an
 * enum value would otherwise author a rule that parses, stores, prefilters, and silently
 * never matches.
 */
export function validatePredicate(p: Predicate): PredicateError[] {
  const errors: PredicateError[] = [];
  const check = (c: Comparison, path: string): void => {
    const spec = PREDICATE_FIELDS[c.field];
    const listOp = c.op === 'in' || c.op === 'not_in';

    if (spec.type === 'number') {
      if (!(NUMBER_OPS as readonly string[]).includes(c.op)) {
        errors.push({ path, message: `${c.field} is numeric; "${c.op}" does not apply` });
      } else if (typeof c.value !== 'number') {
        errors.push({ path, message: `${c.field} must be compared to a number` });
      }
      return;
    }

    if (!(ENUM_OPS as readonly string[]).includes(c.op)) {
      errors.push({ path, message: `${c.field} is an enum; "${c.op}" does not apply` });
      return;
    }
    const values: readonly string[] = spec.values;
    const given = listOp ? c.value : [c.value];
    if (!Array.isArray(given)) {
      errors.push({ path, message: `${c.op} needs a list of values` });
      return;
    }
    for (const v of given) {
      if (typeof v !== 'string' || !values.includes(v)) {
        errors.push({ path, message: `"${String(v)}" is not a valid ${c.field}` });
      }
    }
  };

  p.all.forEach((node, i) => {
    if ('any' in node) node.any.forEach((c, j) => check(c, `all[${i}].any[${j}]`));
    else check(node, `all[${i}]`);
  });
  return errors;
}

/* ------------------------------------------------------------------ TS evaluation */

function fieldValue(scenario: Scenario, field: PredicateField): number | string | null {
  const i = scenario.intake;
  switch (field) {
    // Post-rules values, not the raw form: a rule may test what an earlier rule derived,
    // which is how "decision FICO is now 780" becomes visible to a later condition.
    case 'ficoScore': return scenario.ficoScore;
    case 'docType': return scenario.docType;
    case 'loanPurpose': return scenario.loanPurpose;
    case 'ltv': return scenario.ltv;
    case 'occupancy': return i.occupancy;
    case 'propertyType': return i.propertyType;
    case 'citizenship': return i.citizenship;
    case 'creditEvent': return i.creditEvent;
    case 'state': return i.state;
    case 'loanAmount': return i.loanAmount;
    case 'propertyValue': return i.propertyValue;
    case 'dti': return i.dti;
    case 'dscr': return i.dscr;
    case 'reservesMonths': return i.reservesMonths;
    case 'cashInHand': return i.cashInHand;
    case 'creditEventSeasoningMonths': return i.creditEventSeasoningMonths;
  }
}

function evalComparison(scenario: Scenario, c: Comparison): boolean {
  const actual = fieldValue(scenario, c.field);
  // A null field is unknown, not zero. "reserves under 6 months" must not fire on a
  // broker who simply left reserves blank - that would invent a fact about their file.
  if (actual === null || actual === undefined) return false;

  switch (c.op) {
    case 'eq':  return actual === c.value;
    case 'ne':  return actual !== c.value;
    case 'lt':  return typeof actual === 'number' && actual <   (c.value as number);
    case 'lte': return typeof actual === 'number' && actual <=  (c.value as number);
    case 'gt':  return typeof actual === 'number' && actual >   (c.value as number);
    case 'gte': return typeof actual === 'number' && actual >=  (c.value as number);
    case 'in':     return Array.isArray(c.value) && (c.value as unknown[]).includes(actual);
    case 'not_in': return Array.isArray(c.value) && !(c.value as unknown[]).includes(actual);
    default: return false;
  }
}

export function evaluatePredicate(scenario: Scenario, p: Predicate): boolean {
  return p.all.every((node) =>
    'any' in node
      ? node.any.some((c) => evalComparison(scenario, c))
      : evalComparison(scenario, node));
}

/* ------------------------------------------------------------------ SQL compilation */

const SQL_OP: Record<string, string> = {
  eq: '=', ne: '<>', lt: '<', lte: '<=', gt: '>', gte: '>=',
};

/**
 * Compile to a SQL boolean expression over a jsonb column holding the scenario.
 *
 * Used to prefilter candidate rules in one query rather than loading every active rule
 * and evaluating in TypeScript. Values are emitted as bound parameters, never
 * interpolated: rule payloads are authored through an admin UI, and an admin is not a
 * trust boundary you want to discover you were relying on.
 *
 * CALLER CONTRACT: bind the scenario as an OBJECT (or via sql.json), never as
 * JSON.stringify of one. A stringified value arrives as a jsonb string SCALAR rather
 * than an object, so every `->>` returns null, every predicate evaluates false, and the
 * whole rules layer stops firing - silently, with nothing raised and nothing logged.
 * predicate.test.ts guards this directly because it is invisible in every other way.
 */
export function predicateToSql(
  p: Predicate,
  jsonbColumn: string,
): { sql: string; params: unknown[] } {
  const params: unknown[] = [];
  const bind = (v: unknown): string => { params.push(v); return `$${params.length}`; };

  const one = (c: Comparison): string => {
    const spec = PREDICATE_FIELDS[c.field];
    const path = `${jsonbColumn} ->> ${bind(c.field)}`;
    // The same null-is-unknown rule as the TS side. Without the IS NOT NULL guard SQL
    // would compare NULL and yield NULL, which behaves like false for `lt` but ALSO
    // like false for `ne` - so the two evaluators would disagree exactly on negation.
    const present = `(${jsonbColumn} ? ${bind(c.field)}) AND ${path} IS NOT NULL`;

    if (spec.type === 'number') {
      return `(${present} AND (${path})::numeric ${SQL_OP[c.op]} ${bind(c.value)}::numeric)`;
    }
    switch (c.op) {
      case 'eq':  return `(${present} AND ${path} = ${bind(c.value)})`;
      case 'ne':  return `(${present} AND ${path} <> ${bind(c.value)})`;
      case 'in':  return `(${present} AND ${path} = ANY(${bind(c.value)}::text[]))`;
      case 'not_in': return `(${present} AND NOT (${path} = ANY(${bind(c.value)}::text[])))`;
      default: return 'false';
    }
  };

  if (p.all.length === 0) return { sql: 'true', params };
  const parts = p.all.map((node) =>
    'any' in node ? `(${node.any.map(one).join(' OR ')})` : one(node));
  return { sql: parts.join(' AND '), params };
}

/** The scenario as the flat jsonb the SQL side expects. Keys match PREDICATE_FIELDS. */
export function scenarioToJsonb(scenario: Scenario): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const field of PREDICATE_FIELD_NAMES) {
    const v = fieldValue(scenario, field);
    // Omitted rather than null, so the SQL `?` existence check and the TS null check
    // mean the same thing.
    if (v !== null && v !== undefined) out[field] = v;
  }
  return out;
}
